import { faCircleCheck, faRotateRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer, observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { Domain } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import { ActionMenuCell, Button, TanStackHeader, Time, Tooltip } from '@feathr/components';
import { ContentStatusPill, EmailStatusPill } from '@feathr/extender/components/DomainStatusPill';
import { useEntriConnect, useRefreshDomain } from '@feathr/extender/hooks';
import { useFlags } from '@feathr/extender/state';
import { getIconForAction, TimeFormat } from '@feathr/hooks';

import * as styles from './domainsColumns.css';

interface IRow {
  original: Domain;
}

export const OptionsCell = observer(({ original }: IRow): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const flags = useFlags();
  const { canConnect, openEntri } = useEntriConnect();
  const refreshDomain = useRefreshDomain(original);

  function handleClickViewDetails(): void {
    history.push(original.getItemUrl());
  }

  function handleClickRefresh(): void {
    void refreshDomain();
  }

  function handleClickAuthenticate(): void {
    void openEntri(original);
  }

  function handleClickEdit(): void {
    history.push(original.getItemUrl('edit'));
  }

  return (
    <ActionMenuCell
      items={[
        <ActionMenuCell.Item
          key={'view-details'}
          name={'view-details'}
          onClick={handleClickViewDetails}
          prefix={getIconForAction('info')}
        >
          {t('View details')}
        </ActionMenuCell.Item>,
        original.needsEmailAuthentication(flags.bypassMX) && (
          <ActionMenuCell.Item
            key={'refresh-status'}
            name={'refresh-status'}
            onClick={handleClickRefresh}
            prefix={<FontAwesomeIcon icon={faRotateRight} />}
          >
            {t('Refresh status')}
          </ActionMenuCell.Item>
        ),
        canConnect(original) && (
          <ActionMenuCell.Item
            key={'authenticate-domain'}
            name={'authenticate-domain'}
            onClick={handleClickAuthenticate}
            prefix={<FontAwesomeIcon icon={faCircleCheck} />}
          >
            {t('Authenticate domain')}
          </ActionMenuCell.Item>
        ),
        <ActionMenuCell.Item
          key={'edit'}
          name={'edit-role'}
          onClick={handleClickEdit}
          prefix={getIconForAction('edit')}
        >
          {t('Edit')}
        </ActionMenuCell.Item>,
      ]}
      name={'domain-options'}
    />
  );
});

// Sortable column ids are the fields the list route orders by, so renaming one breaks its sort.
function domainsColumns(t: TFunction): Array<ColumnDef<Domain>> {
  return [
    {
      accessorFn: (row): string => row.get('email_domain'),
      cell: ({ row }: CellContext<Domain, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <Tooltip title={t('View details')}>
              <Button className={styles.domainLink} href={row.original.getItemUrl()} type={'link'}>
                {row.original.displayDomain}
              </Button>
            </Tooltip>
          )}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Domain')} />,
      id: 'email_domain',
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): string => row.get('date_created'),
      cell: ({ row }: CellContext<Domain, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <Time format={TimeFormat.longDate} timestamp={row.original.get('date_created')} />
          )}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Date added')} />,
      id: 'date_created',
      meta: { isFlex: true } satisfies ITanStackColumnMeta,
      // The table opens sorted newest-first, so the first click flips to oldest-first.
      sortDescFirst: true,
    },
    {
      cell: ({ row }: CellContext<Domain, unknown>): JSX.Element => (
        <ContentStatusPill domain={row.original} />
      ),
      enableSorting: false,
      header: (): JSX.Element => (
        <TanStackHeader
          title={t('Content Serving Status')}
          tooltip={t(
            "This domain's status for serving landing pages, partner dashboards, and invite pages.",
          )}
        />
      ),
      id: 'content_status',
      meta: { isFlex: true } satisfies ITanStackColumnMeta,
    },
    {
      cell: ({ row }: CellContext<Domain, unknown>): JSX.Element => (
        <EmailStatusPill domain={row.original} />
      ),
      enableSorting: false,
      header: (): JSX.Element => (
        <TanStackHeader
          title={t('Email Sending Status')}
          tooltip={t("This domain's status for sending emails from Feathr.")}
        />
      ),
      id: 'email_status',
      meta: { isFlex: true } satisfies ITanStackColumnMeta,
    },
    {
      cell: ({ row }: CellContext<Domain, unknown>): JSX.Element => (
        <OptionsCell original={row.original} />
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Options')} />,
      id: 'options',
      meta: { align: 'center', pinned: 'right' } satisfies ITanStackColumnMeta,
      size: 80,
    },
  ];
}

export default domainsColumns;
